// Grade de caminhada de um andar e busca de caminho (A*, 4 vizinhos).
//
// A grade junta o que é pisável de cada sala (roomData), o corredor e as passagens
// abaixo das portas. O vão de parede entre sala e corredor só abre nas portas.

import { roomData } from '../engine/room-data.js';
import type { CampusFloor } from './layout.js';

export interface FloorGrid {
  x0: number;
  y0: number;
  w: number;
  h: number;
  walk: Uint8Array;
}

export function floorGrid(floor: CampusFloor): FloorGrid {
  const { x: x0, y: y0, w, h } = floor.bounds;
  const walk = new Uint8Array(w * h);
  const set = (x: number, y: number) => {
    const gx = x - x0;
    const gy = y - y0;
    if (gx >= 0 && gy >= 0 && gx < w && gy < h) walk[gy * w + gx] = 1;
  };
  for (const placed of [...floor.rooms, floor.corridor]) {
    const data = roomData(placed.map);
    data.walkable.forEach((row, y) => row.forEach((ok, x) => ok && set(placed.x + x, placed.y + y)));
    // a porta é pisável mesmo se um móvel encostar nela
    for (const door of placed.map.doors) set(placed.x + door.x, placed.y + door.y);
  }
  for (const d of floor.doorways) set(d.x, d.y);
  return { x0, y0, w, h, walk };
}

export function walkable(grid: FloorGrid, x: number, y: number): boolean {
  const gx = x - grid.x0;
  const gy = y - grid.y0;
  return gx >= 0 && gy >= 0 && gx < grid.w && gy < grid.h && grid.walk[gy * grid.w + gx] === 1;
}

/**
 * Caminho de (sx, sy) até (tx, ty), em tiles do mundo, sem o ponto de partida.
 * O destino pode ser um tile bloqueado (o sofá onde vai sentar). [] se não houver.
 */
export function findPath(grid: FloorGrid, sx: number, sy: number, tx: number, ty: number, maxNodes = 6000): Array<{ x: number; y: number }> {
  if (sx === tx && sy === ty) return [];
  const key = (x: number, y: number) => (y - grid.y0) * grid.w + (x - grid.x0);
  const start = key(sx, sy);
  const goal = key(tx, ty);
  const open: number[] = [start];
  const came = new Map<number, number>();
  const g = new Map<number, number>([[start, 0]]);
  const f = new Map<number, number>([[start, Math.abs(tx - sx) + Math.abs(ty - sy)]]);
  let visited = 0;
  while (open.length && visited++ < maxNodes) {
    let best = 0;
    for (let i = 1; i < open.length; i++) if ((f.get(open[i]!) ?? Infinity) < (f.get(open[best]!) ?? Infinity)) best = i;
    const current = open.splice(best, 1)[0]!;
    if (current === goal) {
      const path: Array<{ x: number; y: number }> = [];
      let node: number | undefined = current;
      while (node !== undefined && node !== start) {
        path.push({ x: (node % grid.w) + grid.x0, y: Math.floor(node / grid.w) + grid.y0 });
        node = came.get(node);
      }
      return path.reverse();
    }
    const cx = (current % grid.w) + grid.x0;
    const cy = Math.floor(current / grid.w) + grid.y0;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nx = cx + dx;
      const ny = cy + dy;
      const next = key(nx, ny);
      const isGoal = nx === tx && ny === ty;
      if (!isGoal && !walkable(grid, nx, ny)) continue;
      if (nx < grid.x0 || ny < grid.y0 || nx >= grid.x0 + grid.w || ny >= grid.y0 + grid.h) continue;
      const score = (g.get(current) ?? 0) + 1;
      if (score >= (g.get(next) ?? Infinity)) continue;
      came.set(next, current);
      g.set(next, score);
      f.set(next, score + Math.abs(tx - nx) + Math.abs(ty - ny));
      if (!open.includes(next)) open.push(next);
    }
  }
  return [];
}
